// @ts-nocheck
/**
 * lassoOperations.ts
 * ラッソ（投げ縄）選択操作関連メソッド
 * App.vue から切り出し
 */

import {
  drawLassoDeleteFeatures,
  drawLassoDeleteOthers,
} from '@/js/draw'

type VM = any

// ─── 投げ縄選択した地物をまとめて移動 ───
export function startLassoBulkMove(vm: VM) {
  // 選択状態を維持したまま編集モードを有効化
  if (!vm.s_editEnabled) {
    vm.toggleEditEnabled();
  }
  // 投げ縄モードを終了（選択状態は保持、ウィンドウは開いたまま）
  vm.s_isDrawLasso = false;
  // スナックバー表示
  vm.snackbarText = '選択した地物をドラッグして移動できます';
  vm.snackbar = true;
}

// ─── 投げ縄選択した地物の色を一括変更 ───
// チェックマークの色を決定（黄色と透明は濃い色）
export function getCheckColor(colorValue) {
  if (colorValue === 'yellow' || colorValue === 'rgba(0,0,0,0)') {
    return 'grey-darken-2';
  }
  return 'white';
}

export function applyLassoBulkColor(vm: VM, colorName) {
  const source = vm.map01.getSource('click-circle-source');
  const geojson = vm.getSourceData(source);
  if (!geojson || !geojson.features) return;

  // 選択色を更新
  vm.lassoSelectedColor = colorName;

  // 選択された地物の色を変更（透過度をスライダーから取得）
  const opacity = vm.lassoOpacity / 100;
  const colorRgba = vm.colorNameToRgba(colorName, opacity);
  geojson.features.forEach(f => {
    if (f.properties.lassoSelected) {
      // ジオメトリタイプに応じた色設定
      const geoType = f.geometry.type;
      if (geoType === 'Point') {
        f.properties.color = colorName;
        f.properties['point-color'] = colorName;
      } else if (geoType === 'LineString' || geoType === 'MultiLineString') {
        f.properties.color = colorName;
      } else if (geoType === 'Polygon' || geoType === 'MultiPolygon') {
        f.properties.color = colorRgba;
        f.properties.colorName = colorName;
        f.properties['fill-opacity'] = opacity;
      }
      // 連続描画モード時は選択状態をクリア
      if (vm.$store.state.drawContinuousMode) {
        delete f.properties.lassoSelected;
      }
    }
  });

  vm.map01.getSource('click-circle-source').setData(geojson);
  vm.$store.state.clickCircleGeojsonText = JSON.stringify(geojson);
  vm.$store.state.updatePermalinkFire = !vm.$store.state.updatePermalinkFire;

  // 連続描画モード時は選択状態をリセットして次の選択へ
  if (vm.$store.state.drawContinuousMode) {
    vm.$store.state.lassoGeojson = JSON.stringify({ type: 'FeatureCollection', features: [] });
    vm.s_isLassoSelected = false;
  }
}

// カスタムカラーピッカーで選択した色を適用
export function applyLassoCustomColor(vm: VM) {
  vm.lassoSelectedColor = vm.lassoCustomColor;
  vm.applyLassoBulkColor(vm.lassoCustomColor);
}

// ─── 投げ縄選択した地物をまとめて削除 ───
export async function confirmLassoBulkDelete(vm: VM) {
  const count = vm.lassoSelectedCount;
  if (count === 0) {
    vm.snackbarText = '選択されている地物がありません';
    vm.snackbar = true;
    return;
  }

  // 確認ダイアログを表示
  const s = vm.$store.state
  s.confirmProps = {
    title: 'まとめて削除確認',
    okText: '削除',
    cancelText: 'キャンセル',
    persistent: true,
    width: 420,
    color: 'error',
    icon: 'mdi-alert-circle',
    iconColor: 'error',
  }
  s.confirmMessage = `<strong>選択した${count}個の地物を削除します</strong><br>削除後は「元に戻す」ボタンで復元できます。よろしいですか？`

  const confirmed = await new Promise(resolve => {
    s.confirmResolve = resolve
    s.showConfirm = true
  })

  if (!confirmed) return

  vm.saveHistory()
  vm.executeLassoBulkDelete();
}

export function executeLassoBulkDelete(vm: VM) {
  const source = vm.map01.getSource('click-circle-source');
  const geojson = vm.getSourceData(source);
  if (!geojson || !geojson.features) return;

  const count = vm.lassoSelectedCount;
  // 選択されていない地物のみ残す
  geojson.features = geojson.features.filter(f => !f.properties.lassoSelected);

  vm.map01.getSource('click-circle-source').setData(geojson);
  vm.$store.state.clickCircleGeojsonText = JSON.stringify(geojson);
  vm.$store.state.lassoGeojson = JSON.stringify({ type: 'FeatureCollection', features: [] });
  vm.$store.state.updatePermalinkFire = !vm.$store.state.updatePermalinkFire;
  // 選択状態をリセット
  vm.s_isLassoSelected = false;

  // マーカーを更新（削除された地物のマーカーも削除）
  featureCollectionAdd();
  markerAddAndRemove(true);

  vm.snackbarText = `${count}個の地物を削除しました`;
  vm.snackbar = true;

  // Transform Boxを非表示にしてFWを閉じる（確認なし）
  hideTransformBox();
  vm.s_isDrawLasso = false;
  if (vm.s_editEnabled) {
    vm.toggleEditEnabled();
  }
  vm.$store.commit('setFloatingVisible', { id: 'lasso-control-window', visible: false });
  vm.$store.state.lassoBackupGeojson = null;
  vm.angleValue = 0;
  vm.scaleValue = 100;
}

// ─── 選択外の地物をまとめて削除（選択したものだけ残す） ───
export async function confirmLassoDeleteOthers(vm: VM) {
  const selectedCount = vm.lassoSelectedCount;
  if (selectedCount === 0) {
    vm.snackbarText = '選択されている地物がありません';
    vm.snackbar = true;
    return;
  }

  const source = vm.map01.getSource('click-circle-source');
  const geojson = vm.getSourceData(source);
  if (!geojson || !geojson.features) return;

  const totalCount = geojson.features.length;
  const deleteCount = totalCount - selectedCount;

  if (deleteCount === 0) {
    vm.snackbarText = '削除する地物がありません';
    vm.snackbar = true;
    return;
  }

  // 確認ダイアログを表示
  const s = vm.$store.state
  s.confirmProps = {
    title: '他を削除',
    okText: '削除',
    cancelText: 'キャンセル',
    persistent: true,
    width: 420,
    color: 'error',
    icon: 'mdi-alert-circle',
    iconColor: 'error',
  }
  s.confirmMessage = `<strong>選択した${selectedCount}個以外の${deleteCount}個を削除します</strong><br>削除後は「元に戻す」ボタンで復元できます。よろしいですか？`

  const confirmed = await new Promise(resolve => {
    s.confirmResolve = resolve
    s.showConfirm = true
  })

  if (!confirmed) return

  vm.saveHistory()
  vm.executeLassoDeleteOthers();
}

export function executeLassoDeleteOthers(vm: VM) {
  const source = vm.map01.getSource('click-circle-source');
  const geojson = vm.getSourceData(source);
  if (!geojson || !geojson.features) return;

  const totalCount = geojson.features.length;
  const selectedCount = vm.lassoSelectedCount;
  const deleteCount = totalCount - selectedCount;

  // 選択されている地物のみ残す（選択外を削除）
  geojson.features = geojson.features.filter(f => f.properties.lassoSelected);
  // 残った地物は選択状態を維持（色変更などができるように）

  vm.map01.getSource('click-circle-source').setData(geojson);
  vm.$store.state.clickCircleGeojsonText = JSON.stringify(geojson);
  vm.$store.state.updatePermalinkFire = !vm.$store.state.updatePermalinkFire;
  // 選択状態は維持（FWも開いたまま、色変更等が可能）

  // マーカーを更新
  featureCollectionAdd();
  markerAddAndRemove(true);

  // Transform Boxを更新（残った選択地物に合わせて再配置）
  refreshTransformBox(geojson);

  vm.snackbarText = `選択外の${deleteCount}個を削除しました（${selectedCount}個残り）`;
  vm.snackbar = true;
}

export function colorNameToRgba(colorName, opacity = 0.5) {
  const colors = {
    red: `rgba(255,0,0,${opacity})`,
    blue: `rgba(0,0,255,${opacity})`,
    green: `rgba(0,128,0,${opacity})`,
    black: `rgba(0,0,0,${opacity})`,
    orange: `rgba(255,165,0,${opacity})`,
    hotpink: `rgba(255,105,180,${opacity})`,
    yellow: `rgba(255,255,0,${opacity})`,
    purple: `rgba(128,0,128,${opacity})`,
  };
  return colors[colorName] || colors.blue;
}

